import {Injectable} from '@angular/core';

import * as _ from 'lodash';

import {StringUtils} from 'ac-infra';
import {WsEntitiesService} from './communication/ws-entities.service';

export interface EntityInfoResolver {
    prepareEntityObject;
}

type FieldFilter = (fields: any[]) => any[];

@Injectable({providedIn: 'root'})
export class SummaryDetailsResolverService {

    constructor(private wsEntitiesService: WsEntitiesService) {
    }

    resolveFields = (visibleFields: any, entity: any, valuesMap: any, afterResolve?: boolean | FieldFilter) => {
        const visibleFieldsArray = visibleFields === '*' ? Object.getOwnPropertyNames(valuesMap) : visibleFields;

        let resolvedFields = visibleFieldsArray
            .filter((visibleField) => {
                let customFilter = true;
                const preResolveFilter = valuesMap[visibleField] && valuesMap[visibleField].preResolveFilter;

                const value = StringUtils.byString(entity, visibleField);

                if (preResolveFilter !== undefined) {
                    customFilter = _.isFunction(preResolveFilter) ? preResolveFilter(entity, value) : preResolveFilter;
                }

                const haveValidValue = value !== undefined && value !== '' && value !== null;
                const fieldMappedValue = valuesMap[visibleField];
                return customFilter && fieldMappedValue && (fieldMappedValue.outOfObject || haveValidValue);
            }).flatMap((visibleField) => {
                if (valuesMap[visibleField].spread) {
                    entity[visibleField + 'Resolved'] = {};
                    return entity[visibleField].filter((parameter) => {
                       return parameter.value !== null && parameter.value !== '';
                    }).map((parameter) => {
                        this.buildGenericParameter(parameter, entity, visibleField, valuesMap);
                        return visibleField + 'Resolved.' + parameter.name;
                    })
                }
                return visibleField;
            })
            .map((visibleField) => {
                const originalValue = StringUtils.byString(entity, visibleField);
                const value = this.valueResolver(valuesMap, originalValue, entity, visibleField);

                return {
                    ...valuesMap[visibleField],
                    name: visibleField,
                    value,
                    valueClass: valuesMap[visibleField].valueClass && valuesMap[visibleField].valueClass(visibleField, value),
                    template: valuesMap[visibleField].template || '',
                    title: this.resolveTitleByType(valuesMap, originalValue, entity, visibleField),
                    tooltipText: originalValue && valuesMap[visibleField].resolver === 'array' ? this.arrayResolver(originalValue) : undefined,
                    icon: this.iconResolverByType(valuesMap, visibleField, entity, originalValue),
                    rowTemplate: valuesMap[visibleField].rowTemplate
                };
            });

        if (afterResolve) {
            resolvedFields = (typeof afterResolve === 'function') ? afterResolve(resolvedFields) : resolvedFields.filter(resolvedField => !!resolvedField.value);
        }

        return resolvedFields;
    };


    customResolver = (args: {
        value: any;
        entity: any;
        field: string;
        valueProperties: any
    }, customResolver, defaultResolvers) => {
        if (_.isFunction(customResolver)) {
            return customResolver(args.field, args.value, args.entity, args.valueProperties);
        }

        return defaultResolvers();
    };

    resolveTitleByType = (valuesMap: any, value: any, entity: any, field: string) => {
        const valueProperties = valuesMap[field];
        const titleResolver = valueProperties.titleResolver;
        const title = valueProperties.title;

        const defaultTitleResolvers = () => {
            switch (titleResolver) {
                case 'array':
                    const isMany = (Array.isArray(value) && value.length > 1);
                    return this.wordOneOrManyResolver(title, isMany);
                default:
                    return title;
            }
        };

        return this.customResolver({value, entity, field, valueProperties}, titleResolver, defaultTitleResolvers);
    };

    valueResolver = (valuesMap: any, value: any, entity: any, field: string) => {
        const valueProperties = valuesMap[field];
        const resolver = valueProperties.resolver;

        const defaultValueResolvers = () => {
            switch (resolver) {
                case 'boolean':
                    return this.booleanResolver(value);
                case 'array':
                    return this.arrayResolver(value, valueProperties.maxItems || 1, valueProperties.title.toLowerCase());
                case 'array.length':
                    return Array.isArray(value) ? value.length : 0;
                case 'object':
                    return this.objectResolver(value);
                case 'ws.service.to.name':
                    let entitiesIds = value;
                    if (!Array.isArray(value)) {
                        entitiesIds = [value];
                    }

                    const entities = entitiesIds?.length > 0 ? this.wsEntitiesService.getEntitiesArray(valueProperties.entityType, entitiesIds).map((entityObj) => entityObj.name) : [];
                    return entities?.length > 0 ? entities.join(', ') : (valueProperties.nullValue || 'Unknown');
                default:
                    return value;
            }
        };

        return this.customResolver({value, entity, field, valueProperties}, resolver, defaultValueResolvers);
    };

    iconResolverByType = (valuesMap: any, field: string, entity: any, value: any) => {
        const iconClasses = 'fa';
        const icon = valuesMap[field].icon;
        const iconMapper = valuesMap[field].iconMapper;
        const iconShape: string = valuesMap[field].iconShape || 'circle';

        if (_.isFunction(icon)) {
            const iconPostFix = icon(field, value, entity);

            return iconPostFix && iconClasses + ' ' + iconPostFix;
        }
        switch (icon) {
            case 'boolean':
                return iconClasses + ' ' + (this.booleanResolver(value, true) ? 'fa fa-check' : 'fa fa-times');
            case 'status':
                const statusClass = (entity[field + 'Class'] || (iconMapper && iconMapper[value]) || icon + '-' + value || '').toLowerCase();
                return iconClasses + ` fa-${iconShape} ` + statusClass;
            default:
                return;
        }
    };

    booleanResolver = (value: boolean | string, boolean = false) => {
        if (typeof value === 'string') {
            value = ['ACTIVE', 'YES', 'ENABLED', 'OK'].indexOf(value.toUpperCase()) > -1;
        }
        if (boolean) {
            return value;
        }
        return value ? 'Yes' : 'No';
    };

    arrayResolver = (arr: any[], maxItems?: number, postFix = '') => {
        if (Array.isArray(arr) && arr.length > 0) {
            if ((maxItems && arr.length > maxItems)) {
                return `${arr.length} ${postFix}s`;
            }
            return arr.filter((elem) => ![undefined, null].includes(elem)).join(', ');
        } else {
            return 'None';
        }
    };

    objectResolver = (obj: any) => _.map(obj, (value, key: string) => key.toUpperCase() + ': ' + value).join(', ');

    wordOneOrManyResolver = (word: string, isMany = false) => isMany ? word + 's' : word;

    private buildGenericParameter(parameter, entity, visibleField, valuesMap) {
        valuesMap[visibleField + 'Resolved.' + parameter.name] = {title: parameter.name};
        entity[visibleField + 'Resolved'][parameter.name] = parameter.value;

        switch (parameter.type.toString().toLowerCase()) {
            case 'date': {
                valuesMap[visibleField + 'Resolved.' + parameter.name].template = 'time';
                break;
            }
            case 'boolean': {
                valuesMap[visibleField + 'Resolved.' + parameter.name].icon = 'boolean';
                valuesMap[visibleField + 'Resolved.' + parameter.name].resolver = 'boolean';
                break;
            }
        }
    }
}
